// ── Domain constants for Units & Deals ──
// Status palette tuned for Saudi real-estate sales (green=available, amber=reserved, red=sold,
// blue=transferred/أفرغ which is the legal milestone at the notary, indigo=off-plan/خارطة).
export const ST_COLORS = {
  'متاح': '#22c55e',
  'محجوز': '#f59e0b',
  'مباع': '#ef4444',
  'أفرغ': '#3b82f6',
  'بيع على الخارطة': '#6366f1',
  'غير متاح': '#888',
};

export const ST_LABELS = {
  'متاح': { en: 'Available', ar: 'متاح' },
  'محجوز': { en: 'Reserved', ar: 'محجوز' },
  'مباع': { en: 'Sold', ar: 'مباع' },
  'أفرغ': { en: 'Transferred', ar: 'أفرغ' },
  'بيع على الخارطة': { en: 'Off-Plan', ar: 'بيع على الخارطة' },
  'غير متاح': { en: 'Unavailable', ar: 'غير متاح' },
};

// Status order used everywhere (filters, summaries) — matches Saudi sales flow.
export const STATUS_ORDER = ['متاح', 'محجوز', 'مباع', 'أفرغ', 'بيع على الخارطة'];

// Legacy short pipeline — kept for backward compat with older deals that
// don't have a payment method set yet. New deals use CASH_STAGES / BANK_STAGES
// chosen by payment method (كاش vs بنك).
export const STAGES = [
  { en: 'Booking & Earnest', ar: 'الحجز والعربون' },
  { en: 'Sales Contract', ar: 'عقد البيع' },
  { en: 'Bank Financing', ar: 'التمويل البنكي' },
  { en: 'Finalize Procedures', ar: 'إنهاء الإجراءات' },
  { en: 'Title Transfer', ar: 'الأفراغ' },
  { en: 'Key Handover', ar: 'تسليم المفاتيح' },
];

// ── Detailed deal pipelines (per payment method) ──
// Each stage carries optional flags:
//   optional: true  → rendered with "(اختياري)" tag, can be skipped without
//                     blocking the next step (used for the finance refund).
//   meter:    'water' | 'electricity'
//                   → step represents a Yes/No question about a meter
//                     transfer. Only becomes answerable once the pipeline
//                     reaches the "send warranties" step (gated below).
export const CASH_STAGES = [
  { en: 'Pay earnest', ar: 'دفع العربون' },
  { en: 'Send receipt', ar: 'إرسال سند القبض' },
  { en: 'Real-estate tax invoice', ar: 'إصدار فاتورة التصرفات العقارية' },
  { en: 'Send sales contract', ar: 'إرسال عقد البيع' },
  { en: 'Title transfer (الأفراغ)', ar: 'الأفراغ' },
  { en: 'Refund earnest from finance', ar: 'إرسال للمالية لاسترداد العربون', optional: true },
  { en: 'Send warranties', ar: 'إرسال الضمانات' },
  { en: 'Water meter transferred?', ar: 'هل تم نقل عدادات المياه؟', meter: 'water' },
  { en: 'Electricity meter transferred?', ar: 'هل تم نقل عدادات الكهرباء؟', meter: 'electricity' },
  { en: 'Hand over the unit', ar: 'تسليم الوحدة (عقد نموذج التسليم)' },
];

export const BANK_STAGES = [
  { en: 'Pay earnest', ar: 'دفع العربون' },
  { en: 'Send receipt + unit docs', ar: 'إرسال سند القبض ومستندات الوحدة' },
  { en: 'Real-estate tax invoice', ar: 'إصدار فاتورة التصرفات العقارية' },
  { en: 'Send sales contract', ar: 'إرسال عقد البيع' },
  { en: 'Set transfer location', ar: 'تحديد موقع الأفراغ' },
  { en: 'Title transfer (الأفراغ)', ar: 'الأفراغ' },
  { en: 'Refund earnest from finance', ar: 'إرسال للمالية لاسترداد العربون', optional: true },
  { en: 'Send warranties', ar: 'إرسال الضمانات' },
  { en: 'Water meter transferred?', ar: 'هل تم نقل عدادات المياه؟', meter: 'water' },
  { en: 'Electricity meter transferred?', ar: 'هل تم نقل عدادات الكهرباء؟', meter: 'electricity' },
  { en: 'Hand over the unit', ar: 'تسليم الوحدة (عقد نموذج التسليم)' },
];

// The full set of DISTINCT closure steps across both pipelines (cash + bank),
// deduped by English label — the canonical list used to assign each step to a
// company employee (Settings → deal-closure tasks). The step's `en` is its key.
export const ALL_CLOSURE_STEPS = (() => {
  const seen = new Set();
  const out = [];
  [...CASH_STAGES, ...BANK_STAGES].forEach((s) => {
    if (!seen.has(s.en)) { seen.add(s.en); out.push({ en: s.en, ar: s.ar }); }
  });
  return out;
})();
// Sentinel assignee = the sales member who closed the deal (dynamic per deal).
export const DEAL_OWNER = '__deal_owner__';

// Pick the pipeline for a given payment method. Unknown / legacy values
// (including the old 'شيك') default to the cash pipeline since cheque sale
// flow mirrors cash flow more closely than bank.
export const stagesForPayment = (method) => (method === 'بنك' ? BANK_STAGES : CASH_STAGES);

// Index of the "send warranties" stage in a pipeline. Kept for legacy
// callers that gate meter questions; the new checklist treats every step
// uniformly so this is mostly dormant now.
export const warrantyIndex = (stages) => stages.findIndex((s) => s.en === 'Send warranties');

// ── Deal Closure checklist (إغلاق الصفقة) ──
// Each stage in the pipeline now answers a Yes/No question. When every
// non-optional step is 'yes' (and every optional step has any answer at
// all), the deal is considered closed — صفقة مغلقة. The checklist is
// stored on the units row as a JSON string in `deal_checklist`.
export const parseChecklist = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw) || {}; } catch { return {}; }
};

export const checklistAnswered = (stage, value) => {
  if (stage.optional) return value === 'yes' || value === 'no';
  return value === 'yes';
};

export const completedCount = (stages, checklist) =>
  stages.reduce((n, s, i) => n + (checklistAnswered(s, checklist[i]) ? 1 : 0), 0);

export const isDealClosed = (stages, checklist) =>
  stages.every((s, i) => checklistAnswered(s, checklist[i]));

export const MONTHS_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

export const QM = {
  Q1: ['يناير', 'فبراير', 'مارس'],
  Q2: ['أبريل', 'مايو', 'يونيو'],
  Q3: ['يوليو', 'أغسطس', 'سبتمبر'],
  Q4: ['أكتوبر', 'نوفمبر', 'ديسمبر'],
};

// Derive the quarter that a given Arabic month belongs to (e.g. مايو → Q2).
export const quarterFromMonth = (month) => {
  for (const [q, months] of Object.entries(QM)) {
    if (months.includes(month)) return q;
  }
  return 'Q1';
};

// User-facing payment methods. Owner spec: only كاش and بنك. We keep the old
// شيك value functional in stagesForPayment() above so historical records
// aren't broken, but the dropdown only offers the two current options.
export const PAYMENT_METHODS = [
  { value: 'كاش', en: 'Cash', ar: 'كاش' },
  { value: 'بنك', en: 'Bank', ar: 'بنك' },
];

// Display order for known projects. Empty = order projects as they come from the
// tenant's own data (no hardcoded tenant-specific project list).
export const PROJECT_ORDER = [];

// ── Helpers ──
export const fmt = (n) => (Number(n) > 0 ? Number(n).toLocaleString() : '—');
export const fmtFull = (n) => Number(n || 0).toLocaleString();
export const stLabel = (st, lang) => ST_LABELS[st]?.[lang] || st;
export const stColor = (st) => ST_COLORS[st] || '#888';
export const projLabel = (key, t) => {
  if (!key) return '';
  // A lead may carry more than one project (comma-separated); label each part.
  return String(key).split(',').map(k => k.trim()).filter(Boolean).map(k => {
    const tKey = 'wizProject_' + k;
    const v = t(tKey);
    return v === tKey ? k : v;
  }).join('، ');
};

export const sortProjects = (list) =>
  [...list].sort((a, b) => {
    const ia = PROJECT_ORDER.indexOf(a);
    const ib = PROJECT_ORDER.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });

export const sortUnits = (list) =>
  [...list].sort((a, b) => {
    const ia = PROJECT_ORDER.indexOf(a.project);
    const ib = PROJECT_ORDER.indexOf(b.project);
    if (ia !== ib) return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    return (a.unit_code || '').localeCompare(b.unit_code || '');
  });

// Buckets for "is this unit closable" — used to gate the Register Deal CTA.
export const isAvailable = (u) =>
  u.status === 'متاح' || u.status === 'محجوز' || u.status === 'بيع على الخارطة';

// Sale rate = (reserved + sold + transferred) / total, in line with how managers
// report "نسبة البيع" — off-plan inventory is treated as live, not sold yet.
export const computeSaleRate = (stats) => {
  if (!stats?.total) return 0;
  const reserved = stats.byStatus?.['محجوز']?.count || 0;
  const sold = (stats.byStatus?.['مباع']?.count || 0) + (stats.byStatus?.['أفرغ']?.count || 0);
  return Math.round(((sold + reserved) / stats.total) * 100);
};
